import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Snackbar,
  Stack,
  Typography,
  Alert,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CampaignIcon from '@mui/icons-material/Campaign';
import StoreIcon from '@mui/icons-material/Store';
import ShieldIcon from '@mui/icons-material/Shield';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'src/text';
import { ExpenseCategory, useExpenses } from '../expense-context';
import { aiNoticeCard, hiddenFileInput, toastAlert } from '../styles';

const guessCategory = (description: string): ExpenseCategory => {
  const lower = description.toLowerCase();
  if (lower.includes('ads') || lower.includes('meta')) return 'Marketing';
  if (lower.includes('produce') || lower.includes('supply')) return 'Inventory';
  if (lower.includes('staff') || lower.includes('payroll')) return 'Staff';
  if (lower.includes('rent') || lower.includes('util')) return 'Ops';
  return 'Other';
};

export const AiControlHubNotice: React.FC = () => {
  const { addExpense } = useExpenses();
  const { expenseLabels } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const handleCaptureClick = () => {
    inputRef.current?.click();
  };

  const handleFile = (file: File) => {
    const vendor =
      file.name
        .replace(/\.[^/.]+$/, '')
        .replace(/[-_]/g, ' ')
        .trim() || expenseLabels.receiptFallbackName;
    const amount = Number(
      Math.min(180, Math.max(12, file.size / 1200)).toFixed(2),
    );
    const category = guessCategory(vendor);
    const detail = `${vendor} for $${amount.toFixed(2)}`;

    addExpense({
      description: expenseLabels.receiptMemo(vendor),
      category,
      amount,
      date: new Date().toISOString(),
      paymentMethod: 'Scan',
    });
    setToastMessage(expenseLabels.receiptToast(detail));
    setToastOpen(true);
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
      e.target.value = '';
    }
  };

  return (
    <>
      <Paper elevation={0} sx={aiNoticeCard}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2.5}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between">
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <AutoAwesomeIcon color="primary" />
              <Typography variant="overline" color="primary">
                {expenseLabels.aiOverline}
              </Typography>
            </Stack>
            <Typography variant="h6" mt={1} gutterBottom>
              {expenseLabels.aiHeadline}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {expenseLabels.aiDescription}
            </Typography>
            <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" gap={1}>
              <Chip
                icon={<DocumentScannerIcon />}
                label={expenseLabels.chipReadReceipts}
              />
              <Chip
                icon={<StoreIcon />}
                label={expenseLabels.chipPosMatching}
              />
              <Chip
                icon={<CampaignIcon />}
                label={expenseLabels.chipRouteBudgets}
              />
              <Chip
                icon={<ShieldIcon />}
                label={expenseLabels.chipAuditExport}
              />
            </Stack>
          </Box>
          <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <Box
              ref={inputRef}
              component="input"
              type="file"
              accept="image/*"
              capture="environment"
              sx={hiddenFileInput}
              onChange={handleFileChange}
            />
            <Button
              variant="contained"
              startIcon={<PhotoCameraIcon />}
              onClick={handleCaptureClick}>
              {expenseLabels.captureButton}
            </Button>
            <Button
              variant="text"
              startIcon={<RocketLaunchIcon />}
              onClick={() => navigate('/home/assistant')}>
              {expenseLabels.reconcileButton}
            </Button>
          </Stack>
        </Stack>
      </Paper>
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}>
        <Alert
          onClose={() => setToastOpen(false)}
          severity="success"
          variant="filled"
          sx={toastAlert}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
